import { z } from "zod";
import type { CanonicalRecord, ChannelConsent, ConsentStatus, EntityType } from "./canonical";
import { ENTITY_TYPES, normalizeEmail } from "./canonical";
import { fromGenericStatus } from "./consent";
import { TransformSchema, applyTransform, type Value } from "./transforms";

/**
 * Target paths:
 *  - canonical field:      "email", "first_name", "price" …
 *  - custom field:         "custom.loyalty_tier"
 *  - consent:              "consent.email.status" | "consent.email.at" | "consent.email.method" | "consent.email.reason"
 *                          (same for "consent.sms.*")
 *  - list membership:      "lists"
 */
export const FieldMappingSchema = z.object({
  target: z.string().min(1),
  /** One source column, or several (joined by a `concat` transform, or picked first-non-empty). */
  source: z.union([z.string(), z.array(z.string())]).optional(),
  transforms: z.array(TransformSchema).default([]),
});
export type FieldMapping = z.infer<typeof FieldMappingSchema>;

export const MappingSpecSchema = z.object({
  entity: z.enum(ENTITY_TYPES),
  /** Canonical field used as the match key for upserts (email, sku, handle, order_number, path…). */
  keyField: z.string().min(1),
  fields: z.array(FieldMappingSchema).min(1),
  /** Rows whose key is missing are skipped with this reason. */
  requireKey: z.boolean().default(true),
});
export type MappingSpec = z.infer<typeof MappingSpecSchema>;

export type SourceRow = Record<string, unknown>;

export interface MapResult {
  record?: CanonicalRecord;
  skipped?: string;
  errors: string[];
}

function readSource(row: SourceRow, source: FieldMapping["source"]): Value {
  if (source === undefined) return null;
  const get = (col: string): Value => {
    const v = row[col];
    if (v === undefined || v === null) return null;
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === "number" || typeof v === "boolean") return v;
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  if (Array.isArray(source)) return source.map((c) => {
    const v = get(c);
    return v === null ? "" : Array.isArray(v) ? v.join(",") : String(v);
  });
  return get(source);
}

function isEmpty(v: Value): boolean {
  return v === null || (typeof v === "string" && v.trim() === "") || (Array.isArray(v) && v.length === 0);
}

export function applyMapping(row: SourceRow, spec: MappingSpec): MapResult {
  const errors: string[] = [];
  const fields: Record<string, unknown> = {};
  const consent: { email?: Partial<ChannelConsent>; sms?: Partial<ChannelConsent> } = {};
  let lists: string[] | undefined;
  const lineItem: Record<string, unknown> = {};
  const eventProps: Record<string, unknown> = {};

  for (const fm of spec.fields) {
    let v = readSource(row, fm.source);
    // Multi-column source without an explicit concat/list → first non-empty column (before transforms).
    if (Array.isArray(v) && Array.isArray(fm.source) && fm.target !== "lists" && !fm.transforms.some((t) => t.op === "concat" || t.op === "list")) {
      v = v.find((x) => x.trim() !== "") ?? null;
    }
    try {
      for (const t of fm.transforms) v = applyTransform(v, t);
    } catch (e) {
      errors.push(`${fm.target}: ${(e as Error).message}`);
      continue;
    }
    if (isEmpty(v)) continue;

    const consentMatch = /^consent\.(email|sms)\.(status|at|method|reason)$/.exec(fm.target);
    if (consentMatch) {
      const channel = consentMatch[1] as "email" | "sms";
      const prop = consentMatch[2] as "status" | "at" | "method" | "reason";
      const c = (consent[channel] ??= {});
      if (prop === "status") {
        const s = fromGenericStatus(v);
        if (s) c.status = s;
        else errors.push(`${fm.target}: unrecognised consent status "${String(v)}"`);
      } else {
        c[prop] = String(v);
      }
      continue;
    }
    if (fm.target === "lists") {
      const arr = Array.isArray(v) ? v : String(v).split(",");
      lists = [...(lists ?? []), ...arr.map((x) => x.trim()).filter(Boolean)];
      continue;
    }
    if (fm.target.startsWith("property.")) {
      // Event properties: one source column per property (KLV-5).
      eventProps[fm.target.slice("property.".length)] = v;
      continue;
    }
    if (fm.target.startsWith("line_item.")) {
      // Orders exported one row per line item: each row contributes one line item.
      lineItem[fm.target.slice("line_item.".length)] = v;
      continue;
    }
    fields[fm.target] = v;
  }
  if (Object.keys(lineItem).length) fields.line_items = [lineItem];
  if (Object.keys(eventProps).length) {
    const base = typeof fields.properties === "string" && fields.properties.trim() ? safeObject(fields.properties) : (fields.properties as Record<string, unknown> | undefined) ?? {};
    fields.properties = { ...base, ...eventProps };
  }

  if (typeof fields.email === "string") {
    const e = normalizeEmail(fields.email);
    if (e) fields.email = e;
    else {
      errors.push(`email: invalid address "${fields.email}"`);
      delete fields.email;
    }
  }

  const keyRaw = fields[spec.keyField];
  const key = keyRaw === undefined || keyRaw === null ? "" : String(keyRaw).trim();
  if (!key && spec.requireKey) return { skipped: `missing ${spec.keyField}`, errors };

  const record: CanonicalRecord = { entity: spec.entity as EntityType, key, fields };
  const finalConsent: CanonicalRecord["consent"] = {};
  for (const ch of ["email", "sms"] as const) {
    const c = consent[ch];
    if (c?.status) finalConsent[ch] = { ...c, status: c.status as ConsentStatus };
    else if (c && (c.at || c.reason)) errors.push(`consent.${ch}: timestamp/reason given without a status`);
  }
  if (finalConsent.email || finalConsent.sms) record.consent = finalConsent;
  if (lists?.length) record.lists = [...new Set(lists)];
  return { record, errors };
}

/**
 * Merge records that share a key. Used for orders exported one row per line item:
 * line items are concatenated, and other fields keep the first non-empty value.
 * For other entities, the first record wins (later duplicates are reported by the engine).
 */
export function mergeByKey(records: CanonicalRecord[]): CanonicalRecord[] {
  const byKey = new Map<string, CanonicalRecord>();
  const out: CanonicalRecord[] = [];
  for (const r of records) {
    const k = r.key.toLowerCase();
    const prev = byKey.get(k);
    if (!prev || r.entity !== "order") {
      if (!prev) byKey.set(k, r);
      out.push(r);
      continue;
    }
    const prevItems = (prev.fields.line_items as unknown[] | undefined) ?? [];
    const items = (r.fields.line_items as unknown[] | undefined) ?? [];
    for (const [f, v] of Object.entries(r.fields)) {
      if (f === "line_items") continue;
      if (prev.fields[f] === undefined || prev.fields[f] === null || prev.fields[f] === "") prev.fields[f] = v;
    }
    prev.fields.line_items = [...prevItems, ...items];
  }
  return out;
}

function safeObject(s: string): Record<string, unknown> {
  try {
    const o = JSON.parse(s) as unknown;
    return o && typeof o === "object" && !Array.isArray(o) ? (o as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function validateMapping(spec: unknown): { ok: true; spec: MappingSpec } | { ok: false; error: string } {
  const r = MappingSpecSchema.safeParse(spec);
  if (!r.success) return { ok: false, error: r.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ") };
  if (!r.data.fields.some((f) => f.target === r.data.keyField))
    return { ok: false, error: `no field mapped to the key field "${r.data.keyField}"` };
  return { ok: true, spec: r.data };
}
